/**
 * Thin TMDB client that reads the API key out of the user's Firestore
 * settings doc rather than holding one in the PWA bundle. The desktop
 * app is the source of truth for the key — Govu Cloud Account → API
 * Keys — and the PWA just reuses whatever the desktop already pushed.
 *
 * Caches per-tmdbId responses in memory (success only — failures are
 * evicted so they get retried on the next attempt rather than sticking
 * the card on a "TMDB #..." placeholder forever).
 *
 * All outgoing fetches go through a small concurrency limiter so a fresh
 * library load with hundreds of cards doesn't fire hundreds of TMDB
 * requests in parallel and trip the rate limiter.
 */

// Global concurrency limiter for outbound TMDB requests. The PWA used to
// fire every poster lookup at once via Promise.all; under load TMDB returned
// 429s and the failing entries were cached as null forever. 8 in-flight is
// generous (TMDB v3 allows ~50 req/s) but bounded enough to never thrash.
const MAX_CONCURRENT_TMDB_REQUESTS = 8
let activeRequests = 0
const waitingQueue: Array<() => void> = []

async function withTmdbSlot<T>(fn: () => Promise<T>): Promise<T> {
  while (activeRequests >= MAX_CONCURRENT_TMDB_REQUESTS) {
    await new Promise<void>((resolve) => waitingQueue.push(resolve))
  }
  activeRequests++
  try {
    return await fn()
  } finally {
    activeRequests--
    const next = waitingQueue.shift()
    if (next) next()
  }
}

export interface TmdbMovieDetails {
  tmdbId: number
  title: string
  originalTitle: string | null
  tagline: string | null
  year: number | null
  releaseDate: string | null
  posterUrl: string | null
  backdropUrl: string | null
  overview: string | null
  runtime: number | null
  genres: string[]
  rating: number
  voteCount: number
  /** Localised certification for the user's region when available, e.g. "PG-13". */
  certification: string | null
  /** TMDB imdb_id (e.g. "tt0133093") when present. */
  imdbId: string | null
  collection: {
    id: number
    name: string
    posterUrl: string | null
    backdropUrl: string | null
  } | null
}

export interface TmdbSeriesDetails {
  tmdbId: number
  name: string
  year: number | null
  posterUrl: string | null
  backdropUrl: string | null
  overview: string | null
  numberOfSeasons: number
  numberOfEpisodes: number
  genres: string[]
  rating: number
  /** TMDB series status, e.g. 'Returning Series', 'Ended', 'Canceled'. */
  status: string | null
}

export type TmdbSearchResult =
  | {
      kind: 'movie'
      tmdbId: number
      title: string
      year: number | null
      posterUrl: string | null
      overview: string | null
      rating: number
    }
  | {
      kind: 'tv'
      tmdbId: number
      title: string
      year: number | null
      posterUrl: string | null
      overview: string | null
      rating: number
    }
  | {
      kind: 'person'
      tmdbId: number
      name: string
      profileUrl: string | null
      knownFor: string
    }

export interface TmdbPersonCredit {
  tmdbId: number
  title: string
  originalTitle: string | null
  year: number | null
  releaseDate: string | null
  posterUrl: string | null
  mediaType: 'movie' | 'tv'
  /** 'cast' when this credit is an acting role, 'crew' when it's a job. */
  creditKind: 'cast' | 'crew'
  /** Cast character name; null for crew credits. */
  character: string | null
  /** Crew job (Director, Writer, ...); null for cast credits. */
  job: string | null
  /** Crew department (Directing, Writing, Production, ...); null for cast credits. */
  department: string | null
  /** Stable credit_id from TMDB, used as a React key. */
  creditId: string
  rating: number
  voteCount: number
  popularity: number
  overview: string | null
}

export interface TmdbPersonDetails {
  tmdbId: number
  name: string
  profileUrl: string | null
  biography: string | null
  birthday: string | null
  deathday: string | null
  placeOfBirth: string | null
  knownForDepartment: string | null
  imdbId: string | null
  homepage: string | null
  gender: number | null
  alsoKnownAs: string[]
  filmography: TmdbPersonCredit[]
}

export interface TmdbPersonImage {
  filePath: string
  url: string
  width: number
  height: number
  aspectRatio: number
  voteAverage: number
  voteCount: number
}

const movieCache = new Map<number, Promise<TmdbMovieDetails | null>>()
const seriesCache = new Map<number, Promise<TmdbSeriesDetails | null>>()
const personCache = new Map<number, Promise<TmdbPersonDetails | null>>()

function buildAuth(apiKey: string): { headers: Record<string, string>; keyParam: string | null } {
  // TMDB v4 read tokens are JWTs (~hundreds of chars, contain a dot);
  // v3 keys are 32 hex chars. Same heuristic the desktop main process uses.
  const isJwt = apiKey.includes('.') && apiKey.length > 100
  if (isJwt) {
    return {
      headers: { Authorization: `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
      keyParam: null,
    }
  }
  return {
    headers: { 'Content-Type': 'application/json' },
    keyParam: apiKey,
  }
}

function poster(path: string | null | undefined): string | null {
  return path ? `https://image.tmdb.org/t/p/w500${path}` : null
}

function backdrop(path: string | null | undefined): string | null {
  return path ? `https://image.tmdb.org/t/p/w780${path}` : null
}

export async function fetchMovieDetails(
  tmdbId: number,
  apiKey: string,
  language = 'en'
): Promise<TmdbMovieDetails | null> {
  const cached = movieCache.get(tmdbId)
  if (cached) return cached

  const promise = withTmdbSlot(async () => {
    try {
      const { headers, keyParam } = buildAuth(apiKey)
      const params = new URLSearchParams({
        language,
        append_to_response: 'release_dates',
      })
      if (keyParam) params.set('api_key', keyParam)
      const res = await fetch(
        `https://api.themoviedb.org/3/movie/${tmdbId}?${params}`,
        { headers }
      )
      if (!res.ok) return null
      const data = await res.json()
      // Pick a certification. Prefer the user's browser locale region; fall
      // back to US so we always show *something* when one is available.
      const region = (typeof navigator !== 'undefined'
        ? navigator.language?.split('-')[1]
        : null) || 'US'
      const certification = pickCertification(data.release_dates?.results, region)
      const belongs = data.belongs_to_collection
      return {
        tmdbId,
        title: data.title,
        originalTitle: data.original_title ?? null,
        tagline: data.tagline || null,
        year: data.release_date ? parseInt(data.release_date.slice(0, 4), 10) : null,
        releaseDate: data.release_date || null,
        posterUrl: poster(data.poster_path),
        backdropUrl: backdrop(data.backdrop_path),
        overview: data.overview ?? null,
        runtime: data.runtime ?? null,
        genres: (data.genres ?? []).map((g: { name: string }) => g.name),
        rating: data.vote_average ?? 0,
        voteCount: data.vote_count ?? 0,
        certification,
        imdbId: data.imdb_id ?? null,
        collection: belongs
          ? {
              id: belongs.id,
              name: belongs.name,
              posterUrl: poster(belongs.poster_path),
              backdropUrl: backdrop(belongs.backdrop_path),
            }
          : null,
      } satisfies TmdbMovieDetails
    } catch (err) {
      console.warn(`[tmdb] movie ${tmdbId} fetch failed`, err)
      return null
    }
  })

  movieCache.set(tmdbId, promise)
  // Evict null results so the next attempt actually retries instead of
  // pulling a stuck "no data" promise out of the cache forever.
  promise.then((d) => {
    if (d == null) movieCache.delete(tmdbId)
  })
  return promise
}

export interface TmdbSearchPage {
  results: TmdbSearchResult[]
  page: number
  totalPages: number
  totalResults: number
}

/**
 * TMDB /search/multi — returns mixed movies, TV, and people. Results are
 * normalized into a single discriminated union so the search UI can render
 * them in one list with a `kind` switch.
 */
export async function searchMulti(
  query: string,
  apiKey: string,
  language = 'en',
  signal?: AbortSignal
): Promise<TmdbSearchResult[]> {
  const page = await searchMultiPage(query, apiKey, language, 1, signal)
  return page.results
}

/**
 * Paginated variant of `searchMulti`. Matches the shape the PWA search
 * overlay needs for its "Load more" button — returns the normalized
 * results plus TMDB's pagination metadata so the caller can decide
 * whether there's another page to fetch. The underlying endpoint is
 * the same as `searchMulti`; this wrapper just exposes page + totals.
 *
 * Passing an AbortSignal cancels the outgoing fetch if the caller
 * issues a new search before this one resolves (race-condition safety
 * when the user is typing quickly).
 */
export async function searchMultiPage(
  query: string,
  apiKey: string,
  language = 'en',
  page = 1,
  signal?: AbortSignal
): Promise<TmdbSearchPage> {
  const trimmed = query.trim()
  if (!trimmed) {
    return { results: [], page: 1, totalPages: 0, totalResults: 0 }
  }
  try {
    const { headers, keyParam } = buildAuth(apiKey)
    const params = new URLSearchParams({
      query: trimmed,
      language,
      include_adult: 'false',
      page: String(page),
    })
    if (keyParam) params.set('api_key', keyParam)
    const res = await fetch(
      `https://api.themoviedb.org/3/search/multi?${params}`,
      { headers, signal }
    )
    if (!res.ok) {
      return { results: [], page, totalPages: 0, totalResults: 0 }
    }
    const data = await res.json()
    const out: TmdbSearchResult[] = []
    for (const item of data.results ?? []) {
      if (item.media_type === 'movie') {
        out.push({
          kind: 'movie',
          tmdbId: item.id,
          title: item.title,
          year: item.release_date ? parseInt(item.release_date.slice(0, 4), 10) : null,
          posterUrl: poster(item.poster_path),
          overview: item.overview ?? null,
          rating: item.vote_average ?? 0,
        })
      } else if (item.media_type === 'tv') {
        out.push({
          kind: 'tv',
          tmdbId: item.id,
          title: item.name,
          year: item.first_air_date ? parseInt(item.first_air_date.slice(0, 4), 10) : null,
          posterUrl: poster(item.poster_path),
          overview: item.overview ?? null,
          rating: item.vote_average ?? 0,
        })
      } else if (item.media_type === 'person') {
        const knownFor = (item.known_for ?? [])
          .map((k: { title?: string; name?: string }) => k.title || k.name)
          .filter(Boolean)
          .slice(0, 2)
          .join(', ')
        out.push({
          kind: 'person',
          tmdbId: item.id,
          name: item.name,
          profileUrl: item.profile_path
            ? `https://image.tmdb.org/t/p/w185${item.profile_path}`
            : null,
          knownFor,
        })
      }
    }
    return {
      results: out,
      page: data.page ?? page,
      totalPages: data.total_pages ?? 0,
      totalResults: data.total_results ?? out.length,
    }
  } catch (err) {
    if ((err as any)?.name === 'AbortError') {
      throw err
    }
    console.warn('[tmdb] searchMulti failed', err)
    return { results: [], page, totalPages: 0, totalResults: 0 }
  }
}

export async function fetchPersonDetails(
  personId: number,
  apiKey: string,
  language = 'en'
): Promise<TmdbPersonDetails | null> {
  const cached = personCache.get(personId)
  if (cached) return cached

  const promise = withTmdbSlot(async () => {
    try {
      const { headers, keyParam } = buildAuth(apiKey)
      const params = new URLSearchParams({
        language,
        append_to_response: 'combined_credits,external_ids',
      })
      if (keyParam) params.set('api_key', keyParam)
      const res = await fetch(
        `https://api.themoviedb.org/3/person/${personId}?${params}`,
        { headers }
      )
      if (!res.ok) return null
      const data = await res.json()

      const yearFromDate = (d: string | null | undefined): number | null =>
        d ? parseInt(d.slice(0, 4), 10) || null : null
      const isFilmish = (mediaType: string) => mediaType === 'movie' || mediaType === 'tv'

      const rawCast: any[] = data.combined_credits?.cast ?? []
      const rawCrew: any[] = data.combined_credits?.crew ?? []

      const castCredits: TmdbPersonCredit[] = rawCast
        .filter((c) => isFilmish(c.media_type))
        .map((c) => {
          const releaseDate = c.release_date || c.first_air_date || null
          return {
            tmdbId: c.id,
            title: c.title || c.name,
            originalTitle: c.original_title || c.original_name || null,
            year: yearFromDate(releaseDate),
            releaseDate,
            posterUrl: poster(c.poster_path),
            mediaType: c.media_type as 'movie' | 'tv',
            creditKind: 'cast' as const,
            character: c.character ?? null,
            job: null,
            department: null,
            creditId: c.credit_id || `${c.id}-cast-${c.character ?? ''}`,
            rating: c.vote_average ?? 0,
            voteCount: c.vote_count ?? 0,
            popularity: c.popularity ?? 0,
            overview: c.overview ?? null,
          }
        })

      const crewCredits: TmdbPersonCredit[] = rawCrew
        .filter((c) => isFilmish(c.media_type))
        .map((c) => {
          const releaseDate = c.release_date || c.first_air_date || null
          return {
            tmdbId: c.id,
            title: c.title || c.name,
            originalTitle: c.original_title || c.original_name || null,
            year: yearFromDate(releaseDate),
            releaseDate,
            posterUrl: poster(c.poster_path),
            mediaType: c.media_type as 'movie' | 'tv',
            creditKind: 'crew' as const,
            character: null,
            job: c.job ?? null,
            department: c.department ?? null,
            creditId: c.credit_id || `${c.id}-crew-${c.job ?? ''}`,
            rating: c.vote_average ?? 0,
            voteCount: c.vote_count ?? 0,
            popularity: c.popularity ?? 0,
            overview: c.overview ?? null,
          }
        })

      // Sort newest first; modal re-sorts per the user's chosen key.
      const filmography = [...castCredits, ...crewCredits].sort(
        (a, b) => (b.year ?? 0) - (a.year ?? 0)
      )

      return {
        tmdbId: personId,
        name: data.name,
        profileUrl: data.profile_path
          ? `https://image.tmdb.org/t/p/w342${data.profile_path}`
          : null,
        biography: data.biography ?? null,
        birthday: data.birthday ?? null,
        deathday: data.deathday ?? null,
        placeOfBirth: data.place_of_birth ?? null,
        knownForDepartment: data.known_for_department ?? null,
        imdbId: data.imdb_id ?? data.external_ids?.imdb_id ?? null,
        homepage: data.homepage ?? null,
        gender: typeof data.gender === 'number' ? data.gender : null,
        alsoKnownAs: Array.isArray(data.also_known_as) ? data.also_known_as : [],
        filmography,
      } satisfies TmdbPersonDetails
    } catch (err) {
      console.warn(`[tmdb] person ${personId} fetch failed`, err)
      return null
    }
  })

  personCache.set(personId, promise)
  promise.then((d) => {
    if (d == null) personCache.delete(personId)
  })
  return promise
}

const personImagesCache = new Map<number, Promise<TmdbPersonImage[]>>()

export async function fetchPersonImages(
  personId: number,
  apiKey: string
): Promise<TmdbPersonImage[]> {
  const cached = personImagesCache.get(personId)
  if (cached) return cached

  const promise = withTmdbSlot(async () => {
    try {
      const { headers, keyParam } = buildAuth(apiKey)
      const params = new URLSearchParams()
      if (keyParam) params.set('api_key', keyParam)
      const res = await fetch(
        `https://api.themoviedb.org/3/person/${personId}/images?${params}`,
        { headers }
      )
      if (!res.ok) return [] as TmdbPersonImage[]
      const data = await res.json()
      const profiles = Array.isArray(data.profiles) ? data.profiles : []
      return profiles
        .filter((p: any) => typeof p.file_path === 'string')
        .map(
          (p: any): TmdbPersonImage => ({
            filePath: p.file_path,
            url: `https://image.tmdb.org/t/p/w342${p.file_path}`,
            width: p.width ?? 0,
            height: p.height ?? 0,
            aspectRatio: p.aspect_ratio ?? 0,
            voteAverage: p.vote_average ?? 0,
            voteCount: p.vote_count ?? 0,
          })
        )
    } catch (err) {
      console.warn(`[tmdb] person ${personId} images fetch failed`, err)
      return [] as TmdbPersonImage[]
    }
  })

  personImagesCache.set(personId, promise)
  promise.then((d) => {
    if (!d || d.length === 0) personImagesCache.delete(personId)
  })
  return promise
}

export async function fetchSeriesDetails(
  tmdbId: number,
  apiKey: string,
  language = 'en'
): Promise<TmdbSeriesDetails | null> {
  const cached = seriesCache.get(tmdbId)
  if (cached) return cached

  const promise = withTmdbSlot(async () => {
    try {
      const { headers, keyParam } = buildAuth(apiKey)
      const params = new URLSearchParams({ language })
      if (keyParam) params.set('api_key', keyParam)
      const res = await fetch(
        `https://api.themoviedb.org/3/tv/${tmdbId}?${params}`,
        { headers }
      )
      if (!res.ok) return null
      const data = await res.json()
      return {
        tmdbId,
        name: data.name,
        year: data.first_air_date ? parseInt(data.first_air_date.slice(0, 4), 10) : null,
        posterUrl: poster(data.poster_path),
        backdropUrl: backdrop(data.backdrop_path),
        overview: data.overview ?? null,
        numberOfSeasons: data.number_of_seasons ?? 0,
        numberOfEpisodes: data.number_of_episodes ?? 0,
        genres: (data.genres ?? []).map((g: { name: string }) => g.name),
        rating: data.vote_average ?? 0,
        status: data.status ?? null,
      } satisfies TmdbSeriesDetails
    } catch (err) {
      console.warn(`[tmdb] series ${tmdbId} fetch failed`, err)
      return null
    }
  })

  seriesCache.set(tmdbId, promise)
  promise.then((d) => {
    if (d == null) seriesCache.delete(tmdbId)
  })
  return promise
}

// === Detail-view helpers ===

export interface TmdbCastMember {
  tmdbId: number
  name: string
  character: string
  profileUrl: string | null
  order: number
}

export interface TmdbSimilarTitle {
  tmdbId: number
  title: string
  year: number | null
  posterUrl: string | null
  rating: number
  /**
   * Number of TMDB user votes behind the `rating`. Used by the
   * recommendation aggregator to filter out thin-vote oddities so
   * niche films with a handful of 9/10 votes don't dominate the
   * "Top for You" rows.
   */
  voteCount: number
  mediaType: 'movie' | 'tv'
}

export interface TmdbSeason {
  seasonNumber: number
  name: string
  overview: string | null
  posterUrl: string | null
  airDate: string | null
  episodeCount: number
}

export interface TmdbEpisode {
  episodeNumber: number
  seasonNumber: number
  name: string
  overview: string | null
  stillUrl: string | null
  airDate: string | null
  runtime: number | null
  rating: number
}

export async function fetchMovieCast(
  tmdbId: number,
  apiKey: string,
  language = 'en'
): Promise<TmdbCastMember[]> {
  return withTmdbSlot(async () => {
    try {
      const { headers, keyParam } = buildAuth(apiKey)
      const params = new URLSearchParams({ language })
      if (keyParam) params.set('api_key', keyParam)
      const res = await fetch(
        `https://api.themoviedb.org/3/movie/${tmdbId}/credits?${params}`,
        { headers }
      )
      if (!res.ok) return []
      const data = await res.json()
      return ((data.cast ?? []) as any[])
        .slice(0, 20)
        .map((c) => ({
          tmdbId: c.id,
          name: c.name,
          character: c.character ?? '',
          profileUrl: c.profile_path
            ? `https://image.tmdb.org/t/p/w185${c.profile_path}`
            : null,
          order: c.order ?? 0,
        }))
    } catch (err) {
      console.warn(`[tmdb] movie ${tmdbId} cast fetch failed`, err)
      return []
    }
  })
}

export async function fetchSeriesCast(
  tmdbId: number,
  apiKey: string,
  language = 'en'
): Promise<TmdbCastMember[]> {
  return withTmdbSlot(async () => {
    try {
      const { headers, keyParam } = buildAuth(apiKey)
      const params = new URLSearchParams({ language })
      if (keyParam) params.set('api_key', keyParam)
      const res = await fetch(
        `https://api.themoviedb.org/3/tv/${tmdbId}/aggregate_credits?${params}`,
        { headers }
      )
      if (!res.ok) return []
      const data = await res.json()
      return ((data.cast ?? []) as any[])
        .slice(0, 20)
        .map((c) => ({
          tmdbId: c.id,
          name: c.name,
          character: (c.roles?.[0]?.character as string) ?? '',
          profileUrl: c.profile_path
            ? `https://image.tmdb.org/t/p/w185${c.profile_path}`
            : null,
          order: c.order ?? 0,
        }))
    } catch (err) {
      console.warn(`[tmdb] series ${tmdbId} cast fetch failed`, err)
      return []
    }
  })
}

/**
 * Number of /recommendations pages to walk per seed. TMDB returns up
 * to 20 items per page; pages 1-3 cover the highest-signal slice (the
 * tail drops in quality fast). Three pages × 20 items = 60 raw
 * candidates per seed before dedup, library exclusion, and the
 * voteCount filter — enough that even users with very dense libraries
 * get an actually-loadable Top for You row.
 */
const RECOMMENDATIONS_PAGES = 3

export async function fetchSimilarMovies(
  tmdbId: number,
  apiKey: string,
  language = 'en'
): Promise<TmdbSimilarTitle[]> {
  // Fan out N pages in parallel — each call goes through withTmdbSlot
  // so the global concurrency limiter still paces requests.
  const pages = await Promise.all(
    Array.from({ length: RECOMMENDATIONS_PAGES }, (_, i) =>
      fetchOneRecommendationsPage('movie', tmdbId, i + 1, apiKey, language)
    )
  )
  return pages.flat()
}

async function fetchOneRecommendationsPage(
  kind: 'movie' | 'tv',
  tmdbId: number,
  page: number,
  apiKey: string,
  language: string
): Promise<TmdbSimilarTitle[]> {
  return withTmdbSlot(async () => {
    try {
      const { headers, keyParam } = buildAuth(apiKey)
      const params = new URLSearchParams({ language, page: String(page) })
      if (keyParam) params.set('api_key', keyParam)
      const res = await fetch(
        `https://api.themoviedb.org/3/${kind}/${tmdbId}/recommendations?${params}`,
        { headers }
      )
      if (!res.ok) return []
      const data = await res.json()
      // Stop early if TMDB says there are no more pages.
      if (page > (data.total_pages ?? 0)) return []
      return ((data.results ?? []) as any[]).slice(0, 20).map((m) => ({
        tmdbId: m.id,
        title: kind === 'tv' ? m.name : m.title,
        year:
          (kind === 'tv' ? m.first_air_date : m.release_date)
            ? parseInt(
                ((kind === 'tv' ? m.first_air_date : m.release_date) as string).slice(0, 4),
                10
              )
            : null,
        posterUrl: poster(m.poster_path),
        rating: m.vote_average ?? 0,
        voteCount: m.vote_count ?? 0,
        mediaType: kind,
      }))
    } catch (err) {
      console.warn(`[tmdb] ${kind} ${tmdbId} recommendations page ${page} failed`, err)
      return []
    }
  })
}

export async function fetchTrendingWeek(
  apiKey: string,
  language = 'en'
): Promise<TmdbSimilarTitle[]> {
  return withTmdbSlot(async () => {
    try {
      const { headers, keyParam } = buildAuth(apiKey)
      const params = new URLSearchParams({ language })
      if (keyParam) params.set('api_key', keyParam)
      const res = await fetch(
        `https://api.themoviedb.org/3/trending/all/week?${params}`,
        { headers }
      )
      if (!res.ok) return []
      const data = await res.json()
      return ((data.results ?? []) as any[])
        .filter((m) => m.media_type === 'movie' || m.media_type === 'tv')
        .slice(0, 40)
        .map((m) => ({
          tmdbId: m.id,
          title: m.media_type === 'tv' ? m.name : m.title,
          year: (m.release_date || m.first_air_date)
            ? parseInt((m.release_date || m.first_air_date).slice(0, 4), 10)
            : null,
          posterUrl: poster(m.poster_path),
          rating: m.vote_average ?? 0,
          voteCount: m.vote_count ?? 0,
          mediaType: m.media_type as 'movie' | 'tv',
        }))
    } catch (err) {
      console.warn('[tmdb] trending week fetch failed', err)
      return []
    }
  })
}

/**
 * Shared mapper for TMDB movie discovery endpoints (popular, top-rated,
 * now-playing, upcoming, discover/by-genre). Returns the PWA-internal
 * TmdbSimilarTitle shape so every discover row feeds the same card
 * component regardless of source endpoint.
 */
function mapMovieList(results: any[]): TmdbSimilarTitle[] {
  return results
    .filter((m) => !!m && (m.poster_path || m.backdrop_path))
    .map((m) => ({
      tmdbId: m.id,
      title: m.title ?? m.original_title ?? '',
      year: m.release_date ? parseInt(m.release_date.slice(0, 4), 10) : null,
      posterUrl: poster(m.poster_path),
      rating: m.vote_average ?? 0,
      voteCount: m.vote_count ?? 0,
      mediaType: 'movie' as const,
    }))
}

function mapSeriesList(results: any[]): TmdbSimilarTitle[] {
  return results
    .filter((m) => !!m && (m.poster_path || m.backdrop_path))
    .map((m) => ({
      tmdbId: m.id,
      title: m.name ?? m.original_name ?? '',
      year: m.first_air_date ? parseInt(m.first_air_date.slice(0, 4), 10) : null,
      posterUrl: poster(m.poster_path),
      rating: m.vote_average ?? 0,
      voteCount: m.vote_count ?? 0,
      mediaType: 'tv' as const,
    }))
}

/**
 * TMDB /trending/movie/week — movie-only trending list (separate from
 * `fetchTrendingWeek` which mixes movies and TV). Used by the PWA
 * Discover view to power the dedicated "Trending movies" row.
 */
export async function fetchTmdbTrendingMovies(
  apiKey: string,
  language = 'en'
): Promise<TmdbSimilarTitle[]> {
  return withTmdbSlot(async () => {
    try {
      const { headers, keyParam } = buildAuth(apiKey)
      const params = new URLSearchParams({ language })
      if (keyParam) params.set('api_key', keyParam)
      const res = await fetch(
        `https://api.themoviedb.org/3/trending/movie/week?${params}`,
        { headers }
      )
      if (!res.ok) return []
      const data = await res.json()
      return mapMovieList((data.results ?? []) as any[]).slice(0, 24)
    } catch (err) {
      console.warn('[tmdb] trending movies fetch failed', err)
      return []
    }
  })
}

/**
 * TMDB /trending/tv/week — series-only trending list, parallel to the
 * movies row in Discover.
 */
export async function fetchTmdbTrendingSeries(
  apiKey: string,
  language = 'en'
): Promise<TmdbSimilarTitle[]> {
  return withTmdbSlot(async () => {
    try {
      const { headers, keyParam } = buildAuth(apiKey)
      const params = new URLSearchParams({ language })
      if (keyParam) params.set('api_key', keyParam)
      const res = await fetch(
        `https://api.themoviedb.org/3/trending/tv/week?${params}`,
        { headers }
      )
      if (!res.ok) return []
      const data = await res.json()
      return mapSeriesList((data.results ?? []) as any[]).slice(0, 24)
    } catch (err) {
      console.warn('[tmdb] trending series fetch failed', err)
      return []
    }
  })
}

export async function fetchTmdbPopularMovies(
  apiKey: string,
  language = 'en'
): Promise<TmdbSimilarTitle[]> {
  return withTmdbSlot(async () => {
    try {
      const { headers, keyParam } = buildAuth(apiKey)
      const params = new URLSearchParams({ language, page: '1' })
      if (keyParam) params.set('api_key', keyParam)
      const res = await fetch(
        `https://api.themoviedb.org/3/movie/popular?${params}`,
        { headers }
      )
      if (!res.ok) return []
      const data = await res.json()
      return mapMovieList((data.results ?? []) as any[]).slice(0, 24)
    } catch (err) {
      console.warn('[tmdb] popular movies fetch failed', err)
      return []
    }
  })
}

export async function fetchTmdbTopRatedMovies(
  apiKey: string,
  language = 'en'
): Promise<TmdbSimilarTitle[]> {
  return withTmdbSlot(async () => {
    try {
      const { headers, keyParam } = buildAuth(apiKey)
      const params = new URLSearchParams({ language, page: '1' })
      if (keyParam) params.set('api_key', keyParam)
      const res = await fetch(
        `https://api.themoviedb.org/3/movie/top_rated?${params}`,
        { headers }
      )
      if (!res.ok) return []
      const data = await res.json()
      return mapMovieList((data.results ?? []) as any[]).slice(0, 24)
    } catch (err) {
      console.warn('[tmdb] top rated movies fetch failed', err)
      return []
    }
  })
}

export async function fetchTmdbNowPlayingMovies(
  apiKey: string,
  language = 'en'
): Promise<TmdbSimilarTitle[]> {
  return withTmdbSlot(async () => {
    try {
      const { headers, keyParam } = buildAuth(apiKey)
      const params = new URLSearchParams({ language, page: '1' })
      if (keyParam) params.set('api_key', keyParam)
      const res = await fetch(
        `https://api.themoviedb.org/3/movie/now_playing?${params}`,
        { headers }
      )
      if (!res.ok) return []
      const data = await res.json()
      return mapMovieList((data.results ?? []) as any[]).slice(0, 24)
    } catch (err) {
      console.warn('[tmdb] now playing movies fetch failed', err)
      return []
    }
  })
}

export async function fetchTmdbUpcomingMovies(
  apiKey: string,
  language = 'en'
): Promise<TmdbSimilarTitle[]> {
  return withTmdbSlot(async () => {
    try {
      const { headers, keyParam } = buildAuth(apiKey)
      const params = new URLSearchParams({ language, page: '1' })
      if (keyParam) params.set('api_key', keyParam)
      const res = await fetch(
        `https://api.themoviedb.org/3/movie/upcoming?${params}`,
        { headers }
      )
      if (!res.ok) return []
      const data = await res.json()
      return mapMovieList((data.results ?? []) as any[]).slice(0, 24)
    } catch (err) {
      console.warn('[tmdb] upcoming movies fetch failed', err)
      return []
    }
  })
}

export async function fetchTmdbPopularSeries(
  apiKey: string,
  language = 'en'
): Promise<TmdbSimilarTitle[]> {
  return withTmdbSlot(async () => {
    try {
      const { headers, keyParam } = buildAuth(apiKey)
      const params = new URLSearchParams({ language, page: '1' })
      if (keyParam) params.set('api_key', keyParam)
      const res = await fetch(
        `https://api.themoviedb.org/3/tv/popular?${params}`,
        { headers }
      )
      if (!res.ok) return []
      const data = await res.json()
      return mapSeriesList((data.results ?? []) as any[]).slice(0, 24)
    } catch (err) {
      console.warn('[tmdb] popular series fetch failed', err)
      return []
    }
  })
}

export async function fetchTmdbTopRatedSeries(
  apiKey: string,
  language = 'en'
): Promise<TmdbSimilarTitle[]> {
  return withTmdbSlot(async () => {
    try {
      const { headers, keyParam } = buildAuth(apiKey)
      const params = new URLSearchParams({ language, page: '1' })
      if (keyParam) params.set('api_key', keyParam)
      const res = await fetch(
        `https://api.themoviedb.org/3/tv/top_rated?${params}`,
        { headers }
      )
      if (!res.ok) return []
      const data = await res.json()
      return mapSeriesList((data.results ?? []) as any[]).slice(0, 24)
    } catch (err) {
      console.warn('[tmdb] top rated series fetch failed', err)
      return []
    }
  })
}

/**
 * TMDB /discover/movie with a genre filter. Used by the Discover
 * view's per-genre rows (Action, Drama, Sci-Fi, Comedy, etc). Sorted
 * by popularity descending to match desktop's `TOP_RATED_BY_GENRE`
 * visual ordering without tripping TMDB's thin-vote top_rated bias.
 */
export interface TmdbGenrePage {
  items: TmdbSimilarTitle[]
  /** Total pages TMDB reports for the current query. 0 on failure. */
  totalPages: number
}

/**
 * Top-rated movies in a genre, filtered to films with a meaningful
 * vote count so thin-vote oddities don't dominate.
 *
 * Sort: highest TMDB vote_average first. The minimum vote floor is
 * what TMDB's own "top rated" list uses — without it you get
 * niche festival films with a handful of 10/10 votes riding on top.
 *
 * `genreId === 0` means "All Genres" — the `with_genres` filter is
 * dropped and you get the overall top-rated movies.
 *
 * `voteCountFloor` lets the caller tighten the threshold. The home
 * uses 5000 for All Genres (so the overall top-rated list contains
 * only the obvious classics) and 1000 for individual genres (looser
 * because per-genre pools are smaller).
 *
 * Returns `{ items, totalPages }` so the caller can implement
 * progressive pagination: fetch page 1, show it, fetch page 2 on
 * "Load more" click, etc., until `page > totalPages`.
 */
export async function fetchTmdbByGenre(
  genreId: number,
  apiKey: string,
  language = 'en',
  voteCountFloor = 1000,
  page = 1
): Promise<TmdbGenrePage> {
  return withTmdbSlot(async () => {
    try {
      const { headers, keyParam } = buildAuth(apiKey)
      const params = new URLSearchParams({
        language,
        page: String(page),
        sort_by: 'vote_average.desc',
        include_adult: 'false',
        include_video: 'false',
        'vote_count.gte': String(voteCountFloor),
      })
      if (genreId > 0) params.set('with_genres', String(genreId))
      if (keyParam) params.set('api_key', keyParam)
      const res = await fetch(
        `https://api.themoviedb.org/3/discover/movie?${params}`,
        { headers }
      )
      if (!res.ok) return { items: [], totalPages: 0 }
      const data = await res.json()
      return {
        items: mapMovieList((data.results ?? []) as any[]),
        totalPages: typeof data.total_pages === 'number' ? data.total_pages : 0,
      }
    } catch (err) {
      console.warn(`[tmdb] discover by genre ${genreId} fetch failed`, err)
      return { items: [], totalPages: 0 }
    }
  })
}

export async function fetchSimilarSeries(
  tmdbId: number,
  apiKey: string,
  language = 'en'
): Promise<TmdbSimilarTitle[]> {
  const pages = await Promise.all(
    Array.from({ length: RECOMMENDATIONS_PAGES }, (_, i) =>
      fetchOneRecommendationsPage('tv', tmdbId, i + 1, apiKey, language)
    )
  )
  return pages.flat()
}

export async function fetchSeriesSeasons(
  tmdbId: number,
  apiKey: string,
  language = 'en'
): Promise<TmdbSeason[]> {
  return withTmdbSlot(async () => {
    try {
      const { headers, keyParam } = buildAuth(apiKey)
      const params = new URLSearchParams({ language })
      if (keyParam) params.set('api_key', keyParam)
      const res = await fetch(`https://api.themoviedb.org/3/tv/${tmdbId}?${params}`, {
        headers,
      })
      if (!res.ok) return []
      const data = await res.json()
      return ((data.seasons ?? []) as any[])
        .filter((s) => s.season_number != null && s.season_number >= 0)
        .map((s) => ({
          seasonNumber: s.season_number,
          name: s.name ?? `Season ${s.season_number}`,
          overview: s.overview ?? null,
          posterUrl: poster(s.poster_path),
          airDate: s.air_date ?? null,
          episodeCount: s.episode_count ?? 0,
        }))
    } catch (err) {
      console.warn(`[tmdb] series ${tmdbId} seasons fetch failed`, err)
      return []
    }
  })
}

const episodeCache = new Map<string, Promise<TmdbEpisode[]>>()

export async function fetchSeasonEpisodes(
  tmdbId: number,
  seasonNumber: number,
  apiKey: string,
  language = 'en'
): Promise<TmdbEpisode[]> {
  const cacheKey = `${tmdbId}:${seasonNumber}:${language}`
  const cached = episodeCache.get(cacheKey)
  if (cached) return cached

  const promise = withTmdbSlot(async () => {
    try {
      const { headers, keyParam } = buildAuth(apiKey)
      const params = new URLSearchParams({ language })
      if (keyParam) params.set('api_key', keyParam)
      const res = await fetch(
        `https://api.themoviedb.org/3/tv/${tmdbId}/season/${seasonNumber}?${params}`,
        { headers }
      )
      if (!res.ok) return []
      const data = await res.json()
      return ((data.episodes ?? []) as any[]).map((e) => ({
        episodeNumber: e.episode_number,
        seasonNumber: e.season_number,
        name: e.name ?? `Episode ${e.episode_number}`,
        overview: e.overview ?? null,
        stillUrl: e.still_path
          ? `https://image.tmdb.org/t/p/w300${e.still_path}`
          : null,
        airDate: e.air_date ?? null,
        runtime: e.runtime ?? null,
        rating: e.vote_average ?? 0,
      }))
    } catch (err) {
      console.warn(`[tmdb] season ${tmdbId}/${seasonNumber} fetch failed`, err)
      return []
    }
  })

  episodeCache.set(cacheKey, promise)
  promise.then((eps) => {
    if (eps.length === 0) episodeCache.delete(cacheKey)
  })
  return promise
}

// === Movie detail helpers (crew, recommendations, collection) ===

export interface TmdbCrewMember {
  tmdbId: number
  creditId: string
  name: string
  job: string
  department: string
  profileUrl: string | null
}

export interface TmdbCollectionPart {
  tmdbId: number
  title: string
  year: number | null
  posterUrl: string | null
  releaseDate: string | null
  rating: number
}

export interface TmdbCollectionDetails {
  id: number
  name: string
  overview: string | null
  posterUrl: string | null
  backdropUrl: string | null
  parts: TmdbCollectionPart[]
}

/**
 * Fetches the movie crew list. Desktop shows the entire crew grouped by
 * department on its person-detail view; for the detail modal we return
 * everyone and let the UI decide which to surface (director, writers,
 * producers, DoP, composer).
 */
export async function fetchMovieCrew(
  tmdbId: number,
  apiKey: string,
  language = 'en'
): Promise<TmdbCrewMember[]> {
  return withTmdbSlot(async () => {
    try {
      const { headers, keyParam } = buildAuth(apiKey)
      const params = new URLSearchParams({ language })
      if (keyParam) params.set('api_key', keyParam)
      const res = await fetch(
        `https://api.themoviedb.org/3/movie/${tmdbId}/credits?${params}`,
        { headers }
      )
      if (!res.ok) return []
      const data = await res.json()
      return ((data.crew ?? []) as any[]).map((c) => ({
        tmdbId: c.id,
        creditId: c.credit_id,
        name: c.name,
        job: c.job ?? '',
        department: c.department ?? '',
        profileUrl: c.profile_path
          ? `https://image.tmdb.org/t/p/w185${c.profile_path}`
          : null,
      }))
    } catch (err) {
      console.warn(`[tmdb] movie ${tmdbId} crew fetch failed`, err)
      return []
    }
  })
}

/**
 * TMDB /movie/{id}/recommendations is editorialised — it's a different
 * list from /similar, and matches what desktop's "Recommendations" row
 * shows. We keep the existing fetchSimilarMovies (which also hits
 * /recommendations under the hood for parity with desktop) and add this
 * explicit wrapper for clarity.
 */
export async function fetchMovieRecommendations(
  tmdbId: number,
  apiKey: string,
  language = 'en'
): Promise<TmdbSimilarTitle[]> {
  return withTmdbSlot(async () => {
    try {
      const { headers, keyParam } = buildAuth(apiKey)
      const params = new URLSearchParams({ language, page: '1' })
      if (keyParam) params.set('api_key', keyParam)
      const res = await fetch(
        `https://api.themoviedb.org/3/movie/${tmdbId}/recommendations?${params}`,
        { headers }
      )
      if (!res.ok) return []
      const data = await res.json()
      return ((data.results ?? []) as any[]).slice(0, 20).map((m) => ({
        tmdbId: m.id,
        title: m.title,
        year: m.release_date ? parseInt(m.release_date.slice(0, 4), 10) : null,
        posterUrl: poster(m.poster_path),
        rating: m.vote_average ?? 0,
        voteCount: m.vote_count ?? 0,
        mediaType: 'movie' as const,
      }))
    } catch (err) {
      console.warn(`[tmdb] movie ${tmdbId} recommendations fetch failed`, err)
      return []
    }
  })
}

/**
 * Movies which TMDB considers similar to this one (the
 * /movie/{id}/similar endpoint, distinct from the editorialised
 * recommendations list above). Desktop shows both rows separately.
 */
export async function fetchTmdbSimilarMovies(
  tmdbId: number,
  apiKey: string,
  language = 'en'
): Promise<TmdbSimilarTitle[]> {
  return withTmdbSlot(async () => {
    try {
      const { headers, keyParam } = buildAuth(apiKey)
      const params = new URLSearchParams({ language, page: '1' })
      if (keyParam) params.set('api_key', keyParam)
      const res = await fetch(
        `https://api.themoviedb.org/3/movie/${tmdbId}/similar?${params}`,
        { headers }
      )
      if (!res.ok) return []
      const data = await res.json()
      return ((data.results ?? []) as any[]).slice(0, 20).map((m) => ({
        tmdbId: m.id,
        title: m.title,
        year: m.release_date ? parseInt(m.release_date.slice(0, 4), 10) : null,
        posterUrl: poster(m.poster_path),
        rating: m.vote_average ?? 0,
        voteCount: m.vote_count ?? 0,
        mediaType: 'movie' as const,
      }))
    } catch (err) {
      console.warn(`[tmdb] movie ${tmdbId} similar fetch failed`, err)
      return []
    }
  })
}

const collectionCache = new Map<number, Promise<TmdbCollectionDetails | null>>()

export async function fetchCollectionDetails(
  collectionId: number,
  apiKey: string,
  language = 'en'
): Promise<TmdbCollectionDetails | null> {
  const cached = collectionCache.get(collectionId)
  if (cached) return cached

  const promise = withTmdbSlot(async () => {
    try {
      const { headers, keyParam } = buildAuth(apiKey)
      const params = new URLSearchParams({ language })
      if (keyParam) params.set('api_key', keyParam)
      const res = await fetch(
        `https://api.themoviedb.org/3/collection/${collectionId}?${params}`,
        { headers }
      )
      if (!res.ok) return null
      const data = await res.json()
      const parts: TmdbCollectionPart[] = ((data.parts ?? []) as any[])
        .map((p) => ({
          tmdbId: p.id,
          title: p.title,
          year: p.release_date ? parseInt(p.release_date.slice(0, 4), 10) : null,
          posterUrl: poster(p.poster_path),
          releaseDate: p.release_date || null,
          rating: p.vote_average ?? 0,
        }))
        .sort((a, b) => {
          const ad = a.releaseDate ? new Date(a.releaseDate).getTime() : 0
          const bd = b.releaseDate ? new Date(b.releaseDate).getTime() : 0
          return ad - bd
        })
      return {
        id: data.id,
        name: data.name,
        overview: data.overview || null,
        posterUrl: poster(data.poster_path),
        backdropUrl: backdrop(data.backdrop_path),
        parts,
      } satisfies TmdbCollectionDetails
    } catch (err) {
      console.warn(`[tmdb] collection ${collectionId} fetch failed`, err)
      return null
    }
  })

  collectionCache.set(collectionId, promise)
  promise.then((d) => {
    if (d == null) collectionCache.delete(collectionId)
  })
  return promise
}

/**
 * Walk TMDB's /movie/{id}?append_to_response=release_dates response and
 * pick the best certification label for `region`, falling back to US.
 * TMDB returns an array of { iso_3166_1, release_dates: [...] } entries;
 * each release_date has a `certification` which is often blank, so we
 * scan them all and take the first non-empty one.
 */
function pickCertification(
  results: Array<{ iso_3166_1: string; release_dates?: Array<{ certification?: string }> }> | undefined,
  region: string
): string | null {
  if (!results || !Array.isArray(results)) return null
  const pickFrom = (code: string): string | null => {
    const bucket = results.find((r) => r.iso_3166_1 === code)
    if (!bucket) return null
    for (const rd of bucket.release_dates ?? []) {
      if (rd.certification && rd.certification.trim()) return rd.certification.trim()
    }
    return null
  }
  return pickFrom(region) || pickFrom('US') || pickFrom('GB') || null
}
